import { Panel } from '../core/Panel';
import {
  Sim, BuildingType, BUILDING_STAGES, totalBuildingCost,
} from '../../game/simulation';
import { ICON } from '../icons';

export class BuildPanel extends Panel {
  constructor(sim: Sim) { super(sim); }
  id() { return 'build'; }
  title() { return 'Build'; }
  hashInputs() {
    const s = this.sim.state;
    return JSON.stringify({
      sel: s.selectedBuilding,
      placing: s.placing,
      money: Math.floor(s.res.money),
      wood: s.res.wood,
      stone: s.res.stone ?? 0,
      // First-stage affordability also reads wheat from the basket — without
      // it in the hash, the "locked" card state lags when wheat depletes.
      basket: s.res.basket,
    });
  }
  body() {
    const s = this.sim.state;
    const types: { id: BuildingType; label: string; icon: string; sub: string }[] = [
      { id: 'granary', label: 'Granary',  icon: ICON.granary, sub: '+50 storage · 2 stages' },
      { id: 'mill',    label: 'Windmill', icon: ICON.mill,    sub: '5 grain → flour · 3 stages' },
    ];
    const cards = types.map(b => {
      const stages = BUILDING_STAGES[b.id];
      const stage1 = stages[0];
      const total = totalBuildingCost(b.id);
      // Affordability check is against the FIRST stage (the upfront cost on placement)
      const aff = s.res.money  >= (stage1.money ?? 0)
               && s.res.wood   >= (stage1.wood ?? 0)
               && s.res.basket >= (stage1.wheat ?? 0)
               && (s.res.stone ?? 0) >= (stage1.stone ?? 0);
      const active = s.selectedBuilding === b.id;
      const fmtCost = (c: { money?: number; wood?: number; wheat?: number; stone?: number }) => {
        const parts: string[] = [];
        if (c.money) parts.push(`${c.money}<span class="glyph glyph-inline">${ICON.coin}</span>`);
        if (c.wood)  parts.push(`${c.wood}<span class="glyph glyph-inline">${ICON.wood}</span>`);
        if (c.stone) parts.push(`${c.stone}<span class="glyph glyph-inline">${ICON.stone}</span>`);
        if (c.wheat) parts.push(`${c.wheat}<span class="glyph glyph-inline">${ICON.wheat}</span>`);
        return parts.join(' · ') || '—';
      };
      return `
        <div class="ui-card ${active ? 'active' : ''} ${aff ? '' : 'locked'}" data-build="${b.id}">
          <span class="ui-card__icon glyph">${b.icon}</span>
          <div>
            <div class="ui-card__title">${b.label}</div>
            <div class="ui-card__sub">${b.sub}</div>
          </div>
          <span class="ui-card__cost">Now: ${fmtCost(stage1)}</span>
        </div>
        ${active ? `
          <div class="ui-tip" style="margin: -2px 0 8px 0; font-size: 10px;">
            ${stages.map((st, i) => `Stage ${i + 1}: ${fmtCost(st)}`).join(' • ')}<br>
            <strong>Total to build:</strong> ${fmtCost(total)}
          </div>
        ` : ''}`;
    }).join('');
    const placing = !!s.placing;
    return `
      ${cards}
      <div class="ui-tip">Placement pays just the first stage. Click the building site to fund each next stage as you go.</div>
      <button class="ui-btn ui-btn--primary ui-btn--block" data-act="place" ${s.selectedBuilding ? '' : 'disabled'}>
        ${placing ? 'Placing… (ESC to cancel)' : s.selectedBuilding ? `Place ${s.selectedBuilding} foundation` : 'Pick a building'}
      </button>
    `;
  }
  onClick(e: Event) {
    const t = e.target as HTMLElement;
    const bEl = t.closest('[data-build]') as HTMLElement | null;
    if (bEl) {
      if (bEl.classList.contains('locked')) {
        this.sim.bus.emit({ type: 'blocked', reason: 'Cannot afford that yet.' });
        return;
      }
      this.sim.selectBuilding(bEl.dataset.build as BuildingType);
      return;
    }
    const actEl = t.closest('[data-act]') as HTMLElement | null;
    if (actEl?.dataset.act === 'place' && !(actEl as HTMLButtonElement).disabled) {
      this.sim.enterPlacementMode();
    }
  }
}
